import type {
  OpenMeteoPointCurrentData,
  OpenMeteoPointDailyData,
  OpenMeteoPointHourlyData,
} from '@/features/dashboard/types/openMeteo.types';
import { baseApi } from '@/stores/api';

/** Gateway: `{ statusCode, message, data: <payload> }` — gỡ lớp `data` lồng nhau. */
function peelGatewayEnvelope(raw: unknown): unknown {
  let cur: unknown = raw;
  for (let depth = 0; depth < 8; depth++) {
    if (cur && typeof cur === 'object') {
      const o = cur as Record<string, unknown>;
      if ('data' in o && o.data !== undefined) {
        cur = o.data;
        continue;
      }
    }
    break;
  }
  return cur;
}

function isOpenMeteoPointCurrentData(x: unknown): x is OpenMeteoPointCurrentData {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (typeof o.latitude !== 'number' || typeof o.longitude !== 'number') return false;
  const c = o.current;
  if (!c || typeof c !== 'object') return false;
  return typeof (c as Record<string, unknown>).temperature_2m === 'number';
}

function isOpenMeteoPointHourlyData(x: unknown): x is OpenMeteoPointHourlyData {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (typeof o.latitude !== 'number' || typeof o.longitude !== 'number') return false;
  const h = o.hourly;
  if (!h || typeof h !== 'object') return false;
  const time = (h as Record<string, unknown>).time;
  return Array.isArray(time) && time.length > 0;
}

function isOpenMeteoPointDailyData(x: unknown): x is OpenMeteoPointDailyData {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  if (typeof o.latitude !== 'number' || typeof o.longitude !== 'number') return false;
  const d = o.daily;
  if (!d || typeof d !== 'object') return false;
  const time = (d as Record<string, unknown>).time;
  return Array.isArray(time) && time.length > 0;
}

function transformCurrent(raw: unknown): OpenMeteoPointCurrentData | null {
  const peeled = peelGatewayEnvelope(raw);
  return isOpenMeteoPointCurrentData(peeled) ? peeled : null;
}

function transformHourly(raw: unknown): OpenMeteoPointHourlyData | null {
  const peeled = peelGatewayEnvelope(raw);
  return isOpenMeteoPointHourlyData(peeled) ? peeled : null;
}

function transformDaily(raw: unknown): OpenMeteoPointDailyData | null {
  const peeled = peelGatewayEnvelope(raw);
  return isOpenMeteoPointDailyData(peeled) ? peeled : null;
}

export interface OpenMeteoPointLatLonArgs {
  lat: string;
  lon: string;
}

const COMMON_PARAMS = {
  timezone: 'auto',
  temperature_unit: 'celsius',
  wind_speed_unit: 'ms',
  precipitation_unit: 'mm',
} as const;

export const openMeteoApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOpenMeteoPointCurrent: builder.query<OpenMeteoPointCurrentData | null, OpenMeteoPointLatLonArgs>({
      query: ({ lat, lon }) => ({
        url: '/api/weather/point-open-meteo',
        params: {
          lat,
          lon,
          kind: 'current',
          forecast_days: '7',
          ...COMMON_PARAMS,
        },
      }),
      transformResponse: (raw: unknown) => transformCurrent(raw),
      providesTags: ['Weather'],
      keepUnusedDataFor: 600,
    }),
    getOpenMeteoPointHourly: builder.query<OpenMeteoPointHourlyData | null, OpenMeteoPointLatLonArgs>({
      query: ({ lat, lon }) => ({
        url: '/api/weather/point-open-meteo',
        params: {
          lat,
          lon,
          kind: 'hourly',
          forecast_days: '8',
          ...COMMON_PARAMS,
        },
      }),
      transformResponse: (raw: unknown) => transformHourly(raw),
      providesTags: ['Weather'],
      keepUnusedDataFor: 600,
    }),
    getOpenMeteoPointDaily: builder.query<OpenMeteoPointDailyData | null, OpenMeteoPointLatLonArgs>({
      query: ({ lat, lon }) => ({
        url: '/api/weather/point-open-meteo',
        params: {
          lat,
          lon,
          kind: 'daily',
          forecast_days: '8',
          ...COMMON_PARAMS,
        },
      }),
      transformResponse: (raw: unknown) => transformDaily(raw),
      providesTags: ['Weather'],
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useGetOpenMeteoPointCurrentQuery,
  useGetOpenMeteoPointHourlyQuery,
  useGetOpenMeteoPointDailyQuery,
} = openMeteoApi;
